import {
  add,
  differenceInDays,
  endOfDay,
  endOfMonth,
  format,
  getMonth,
  getYear,
  intervalToDuration,
  isAfter,
  isBefore,
  isEqual,
  setYear,
  startOfMonth,
} from 'date-fns'

import { type Interval, IntervalUnit, normalizeIntervalUnit } from './interval'
import type { TimeRange } from './time-range'
import { type Timespan, yearAndMonthToTimespan } from './timespan'

export { de, enGB, es, fr, it, nl } from 'date-fns/locale'

export { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

export { formatDateForICS } from './ics'
export { default as sub } from 'date-fns/sub'
export { default as add } from 'date-fns/add'
export { default as isFirstDayOfMonth } from 'date-fns/isFirstDayOfMonth'
export { default as isLastDayOfMonth } from 'date-fns/isLastDayOfMonth'
export { default as setISODay } from 'date-fns/setISODay'
export { default as getEndOfMonth } from 'date-fns/lastDayOfMonth'
export { default as isEndOfMonth } from 'date-fns/isLastDayOfMonth'
export { default as setDate } from 'date-fns/setDate'
export { default as setDay } from 'date-fns/setDay'
export { default as getDate } from 'date-fns/getDate'
export { default as startOfMonth } from 'date-fns/startOfMonth'
export { default as format } from 'date-fns/format'
export { default as addDays } from 'date-fns/addDays'
export { default as subDays } from 'date-fns/subDays'
export { default as addWeeks } from 'date-fns/addWeeks'
export { default as subWeeks } from 'date-fns/subWeeks'
export { default as addYears } from 'date-fns/addYears'
export { default as startOfDay } from 'date-fns/startOfDay'
export { default as endOfDay } from 'date-fns/endOfDay'
export { default as startOfTomorrow } from 'date-fns/startOfTomorrow'
export { default as getDay } from 'date-fns/getDay'
export { default as setYear } from 'date-fns/setYear'
export { default as isWithinInterval } from 'date-fns/isWithinInterval'
export { default as isAfter } from 'date-fns/isAfter'
export { default as isBefore } from 'date-fns/isBefore'
export { default as isSameDay } from 'date-fns/isSameDay'
export { default as differenceInDays } from 'date-fns/differenceInDays'
export { default as differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
export { default as differenceInMonths } from 'date-fns/differenceInMonths'
export { default as eachDayOfInterval } from 'date-fns/eachDayOfInterval'
export { default as eachMonthOfInterval } from 'date-fns/eachMonthOfInterval'
export { default as startOfWeek } from 'date-fns/startOfWeek'
export { default as endOfMonth } from 'date-fns/endOfMonth'
export { default as endOfWeek } from 'date-fns/endOfWeek'
export { default as startOfQuarter } from 'date-fns/startOfQuarter'
export { default as endOfQuarter } from 'date-fns/endOfQuarter'
export { default as startOfYear } from 'date-fns/startOfYear'
export { default as endOfYear } from 'date-fns/endOfYear'
export type { Duration, Locale } from 'date-fns'
export { default as addMinutes } from 'date-fns/addMinutes'
export { default as subMinutes } from 'date-fns/subMinutes'
export { default as addHours } from 'date-fns/addHours'
export { default as subHours } from 'date-fns/subHours'
export { default as addMonths } from 'date-fns/addMonths'
export { default as subMonths } from 'date-fns/subMonths'
export { default as differenceInMinutes } from 'date-fns/differenceInMinutes'
export { default as isToday } from 'date-fns/isToday'
export { default as isTomorrow } from 'date-fns/isTomorrow'
export { default as isYesterday } from 'date-fns/isYesterday'
export { default as parseISO } from 'date-fns/parseISO'
export { default as parse } from 'date-fns/parse'
export { default as isValid } from 'date-fns/isValid'
export { default as min } from 'date-fns/min'
export { default as max } from 'date-fns/max'
export { default as getYear } from 'date-fns/getYear'
export { default as intervalToDuration } from 'date-fns/intervalToDuration'
export { default as isLeapYear } from 'date-fns/isLeapYear'
export { default as compareAsc } from 'date-fns/compareAsc'
export { default as compareDesc } from 'date-fns/compareDesc'
export { default as getUnixTime } from 'date-fns/getUnixTime'

export { differenceInDaysInclusive } from './differenceInDaysInclusive'

interface GetDateWithDefaultYearOptions {
  date?: Date
  formatted?: boolean
}

export const differenceInDaysWithTimezone = (start: Date, end: Date) => {
  const addTimeZoneOffset = (d: Date) => new Date(d.setHours(d.getHours() + d.getTimezoneOffset() / 60))
  return differenceInDays(addTimeZoneOffset(start), addTimeZoneOffset(end))
}

export const getDateWithDefaultYear = (options: GetDateWithDefaultYearOptions) => {
  const { date, formatted } = options
  const defaultYear = 2000
  const dateToUse = date ? new Date(date) : new Date()
  dateToUse.setUTCHours(0, 0, 0, 0)
  const dateWithDefaultYear = setYear(dateToUse, defaultYear)
  return formatted ? format(dateWithDefaultYear, 'yyyy-MM-dd') : dateWithDefaultYear
}

export const dayAfter = (date: Date): Date => add(date, { days: 1 })

export const isAfterOrEqual = (dateToCheck: Date, start: Date): boolean =>
  isAfter(dateToCheck, start) || isEqual(dateToCheck, start)

export type DurationUnit =
  | 'minute'
  | 'minutes'
  | 'hour'
  | 'hours'
  | 'day'
  | 'days'
  | 'week'
  | 'weeks'
  | 'month'
  | 'months'
  | 'year'
  | 'years'

export const mapUnitsToDuration = (value: number, unit: DurationUnit): Duration => {
  switch (unit.toLocaleLowerCase()) {
    case 'minute':
    case 'minutes':
      return { minutes: value }
    case 'hour':
    case 'hours':
      return { hours: value }
    case 'day':
    case 'days':
      return { days: value }
    case 'week':
    case 'weeks':
      return { weeks: value }
    case 'month':
    case 'months':
      return { months: value }
    case 'year':
    case 'years':
      return { years: value }
    default:
      throw new Error(`invalid interval unit \`${unit}\``)
  }
}

export enum RelativeToType {
  Before = 'before',
  After = 'after',
}

/**
 * Returns a new date x units before or after a given Date
 */
export function getDateRelativeFromOtherDate({ value, unit }: Interval, type: RelativeToType, date: Date): Date {
  // just to be safe we ensure unit is converted to enum type as sometimes other unit strings are used in db
  unit = normalizeIntervalUnit(unit)

  // clone date object, we don't want to alter the passed one
  const newDate = new Date(date)

  // flip value depending on whether we want to add or subtract units
  value *= type === RelativeToType.After ? 1 : -1

  switch (unit) {
    case IntervalUnit.Minutes:
      newDate.setHours(newDate.getHours(), newDate.getMinutes() + value)
      break
    case IntervalUnit.Hours:
      newDate.setHours(newDate.getHours() + value)
      break
    case IntervalUnit.Days:
      newDate.setDate(newDate.getDate() + value)
      break
    case IntervalUnit.Weeks:
      newDate.setDate(newDate.getDate() + value * 7)
      break
    case IntervalUnit.Months:
      return add(newDate, { months: value })
    case IntervalUnit.Years:
      return add(newDate, { years: value })
  }

  return newDate
}

export function getMonthsWithinTimespan(timespan: Timespan): Array<Timespan> {
  const months = []
  const currentDate = new Date(timespan.from.getTime())

  while (currentDate <= endOfMonth(timespan.until)) {
    const year = getYear(currentDate)
    const month = getMonth(currentDate)
    months.push(yearAndMonthToTimespan(year, month + 1))
    currentDate.setMonth(month + 1)
  }

  return months
}

/**
 * Sets a Date to the beginning of the week (monday)
 */
export function setDateToStartOfWeek(date: Date): Date {
  // 0 = sunday, 1 = monday ... 6 = saturday
  const weekDay = date.getDay()
  const daysAfterMonday = weekDay === 0 ? 6 : weekDay - 1
  date.setDate(date.getDate() - daysAfterMonday)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Sets a Date to the beginning of the month
 */
export function setDateToStartOfMonth(date: Date): Date {
  date.setDate(1)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * checks if a date is after another date based on the end of the day
 */
export const isAfterBasedOnEndOfDay = (then: Date, now: Date): boolean =>
  isAfter(endOfDay(new Date(then)), endOfDay(new Date(now)))

export const isBeforeBasedOnEndOfDay = (then: Date, now: Date): boolean =>
  isBefore(endOfDay(new Date(then)), endOfDay(new Date(now)))

export const isValidDate = (d: Date): boolean => !isNaN(d.getTime())

export function getMonthsByLocale(locale: string): Array<string> {
  const date = startOfMonth(new Date())

  return Array.from({ length: 12 }, (_, monthNumber) => {
    date.setMonth(monthNumber)
    return date.toLocaleString(locale, { month: 'long' })
  })
}

export function getFormattedDurationOfRawTimespan(rawStart: string, rawEnd: string): string {
  const formatPad2 = (value: number) => value.toString().padStart(2, '0')
  const today = format(new Date(), 'yyyy-MM-dd')
  const tomorrow = format(add(new Date(), { days: 1 }), 'yyyy-MM-dd')
  const start = new Date(`${today} ${rawStart}:00`)
  const end = new Date(`${rawStart > rawEnd ? tomorrow : today} ${rawEnd}:00`)
  const rawDuration = intervalToDuration({ start, end })
  return `${rawDuration.hours ? formatPad2(rawDuration.hours) : '00'}:${
    rawDuration.minutes ? formatPad2(rawDuration.minutes) : '00'
  }`
}

export function getNowUntilTomorrowRange(): TimeRange {
  const start = new Date()
  const end = endOfDay(add(start, { days: 1 }))
  return { start, end }
}

export { yearAndMonthToTimespan } from './timespan'
export { yearAndQuarterToTimespan } from './timespan'
export { yearToTimespan } from './timespan'
export { splitTimespanInMonths } from './timespan'
export type { Timespan, OpenTimespan } from './timespan'
export { isWithin, overlaps } from './timespan'
export type { TimeOfDayRange } from './time-of-day-range'
export { normalizeTimeOfDay, timeOfDayRangesOverlap } from './time-of-day-range'
export { minutesBetweenMinutesOfDay, minutesBetweenTimesOfDay, timeOfDayToMinutes } from './time-of-day'
export * from './time-range'
export * from './interval'
export { getQuarter } from './quarter'
export { parseIsoDateTime, toIsoDate, toShortTime } from './db-strings'
export {
  dayOfWeekOf,
  instantAtMinuteOfDay,
  isRealWallClockSpan,
  type LocalDate,
  localDateOf,
  MINUTES_PER_DAY,
  minuteOfDayAt,
} from './local-date'
